"use strict";

const $ = require("jquery");
const ldcEvents = require("js/event_helper.js");

const TAG_NAME = "ldc-editor-pdf-viewer";
const NAME = "pdf_view";

import { LDCAbstractEditor } from "./abstract_editor.js";

const MIN_WIDTH = 300;
const MIN_HEIGHT = 300;

function trackResize(e, rootElement) {
    let resizerBox = $(rootElement);
    let fullDocument = $(document);

    // add event to .resizer-box
    resizerBox.on("mousedown", ".resizer-box", (e) => {
        console.log("Track resize");

        let initialWidth = resizerBox.width();
        let initialHeight = resizerBox.height();

        let lastValidWidth = initialWidth;
        let lastValidHeight = initialHeight;

        let lastX = e.screenX;
        let lastY = e.screenY;

        function endResize() {
            // clear event - we don't need to track the resize anymore
            $(document).off("mousemove.resize");
            $(document).off("mouseup.resize");
        }

        // while resizing - adjust the current size
        // .resize allows us to add this event and remove it later on!
        fullDocument.on("mousemove.resize", (mouseMoveEvent) => {
            mouseMoveEvent.stopPropagation();
            mouseMoveEvent.preventDefault();

            // it is sometimes possible that the resize does not stop when the mouse button is released,
            // but I cannot reliably reproduce this. So this is a security abort that detects if no
            // button is pressed but a mouse move is still being handled, because that means that we
            // did not handle the mouse up event for some reason and thus this listener has not been
            // unsubscribed yet.
            if (mouseMoveEvent.originalEvent.buttons == 0) {
                console.warn(
                    "Detected mouse move event on resize but with no button down, aborting resize with current size!",
                );
                endResize();
                return;
            }

            let newX = mouseMoveEvent.screenX;
            let newY = mouseMoveEvent.screenY;

            let diffX = newX - lastX;
            let diffY = newY - lastY;

            // save current width, in case we need to reset the size because the box cannot
            // be made any smaller
            let currentWidth = Math.max(initialWidth + diffX, MIN_WIDTH);
            let currentHeight = Math.max(initialHeight + diffY, MIN_HEIGHT);

            // set new size
            resizerBox.width(currentWidth);
            resizerBox.height(currentHeight);

            // used to check if any cards are "exiting" the view area
            let bottomMax = false;
            let rightMax = false;

            // check if we need to reset the height or width. Take the actual mouse movement into account,
            // otherwise it will not be possible to increase the size of the view area again after
            // a card has touched a border of the view area
            const resetHeight = bottomMax && diffY <= 0;
            const resetWidth = rightMax && diffX <= 0;

            // reset both the width of the view area, as well as the computed new width and height
            // if no reset is necessary, and we are dragging the top left corner, move the cards
            if (resetWidth) {
                resizerBox.width(lastValidWidth);
            } else {
                // remember last valid width (so that we don't have to reset to original,
                // we can reset to last previous width)
                lastValidWidth = currentWidth;
            }

            if (resetHeight) {
                resizerBox.height(lastValidHeight);
            } else {
                lastValidHeight = currentHeight;
            }
        });

        fullDocument.on("mouseup.resize", (_x) => {
            endResize();
        });
    });
}

const INITIAL_WIDTH = 600;
const INITIAL_HEIGHT = 800;

class LDCPdfViewer extends LDCAbstractEditor {
    constructor() {
        super(NAME);
        this.__pdf_file = null;

        this.__rootElement = null;
        this.__pdfContainer = null;
    }

    connectedCallback() {
        if (this.__rootElement == null) {
            console.log("LDCPdfViewer: Start");

            // from the pdf_viewer.html.component file
            const template = document.getElementById("ldc-pdf-viewer");
            let root = template.content.cloneNode(true);
            this.__rootElement = root.querySelector(".root");
            this.__rootElement.style.width = INITIAL_WIDTH;
            this.__rootElement.style.height = INITIAL_HEIGHT;

            this.__pdfContainer = this.__rootElement.querySelector(".pdfContainer");

            // we assume two different modes: show and edit
            const purpose = $(this).attr("data-purpose");
            let isShow = purpose === "show" || purpose === "static_html";
            if (isShow) {
                // this is set in the card_content.html.heex file
                this.__pdf_file = $(this).attr("data-pdf-file");
                let initialPage = $(this).attr("data-selected-page");
                let width = $(this).attr("data-width");
                let height = $(this).attr("data-height");

                PDFObject.embed(this.__pdf_file, this.__pdfContainer, {
                    forcePDFJS: true,
                    PDFJS_URL: "/deps/pdfjs/web/viewer.html",
                    page: initialPage != "" ? parseInt(initialPage) : 0,
                });

                this.__rootElement.style.width = width;
                this.__rootElement.style.height = height;
                console.log({ width, height });

                // hide the remove button
                let removeButton = this.__rootElement.querySelector(".removeButton");
                removeButton.style.display = "none";
            } else {
                // add action on remove
                let removeButton = this.__rootElement.querySelector(".removeButton");
                removeButton.addEventListener("click", () => {
                    ldcEvents.dispatch(ldcEvents.events.deleteEditor, this, { instance: this });
                });

                // add resizer actions
                let querySelector = this.__rootElement.querySelector(".resizer-box.bottom-right");
                $(querySelector).on("mousedown", (e) => {
                    trackResize(e, this.__rootElement);
                });
            }

            // $(this).append(container);
            $(this).append(root);
            console.log("ldc-editor-pdf-viewer: connectedCallback()");
        }
    }

    __doGetContentForSaving(cardType) {
        // get pdf page:
        let pdfObject = this.__pdfContainer.querySelector(".pdfobject");
        let pageNumber = pdfObject.contentDocument.querySelector("#pageNumber").value;
        return {
            content: "",
            pdf_file: this.__pdf_file,
            type: NAME,
            width: this.__rootElement.style.width,
            height: this.__rootElement.style.height,
            selected_page: parseInt(pageNumber),
        };
    }

    __mementoFromState() {
        return {
            pdf_file: this.__pdf_file,
            width: this.__rootElement.style.width,
            height: this.__rootElement.style.height,
        };
    }

    __mementoFromCardContent(card, content) {
        console.log("LDCPdfViewer: __mementoFromCardContent()", card, content);
        return {
            pdf_file: content.meta.pdf_file,
            height: content.meta.height,
            width: content.meta.width,
        };
    }

    __setState(memento) {
        this.__pdf_file = memento.pdf_file;
        console.log("LDCPdfViewer set memento", this.__mementoFromState());

        // do nothing
        PDFObject.embed(this.__pdf_file, this.__pdfContainer, {
            forcePDFJS: true,
            PDFJS_URL: "/deps/pdfjs/web/viewer.html",
        });

        this.__rootElement.style.width = memento.width;
        this.__rootElement.style.height = memento.height;
    }

    __hasBeenModified(memento) {
        // for now the pdf instance is read-only in a way
        return false;
    }

    focus() {}

    getEmptyMemento() {
        return {
            pdf_file: null,
        };
    }
}

window.customElements.define(TAG_NAME, LDCPdfViewer);

export const EDITOR_INFO = {
    name: NAME,
    tag: TAG_NAME,
};
